import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import AuthContext from "./AuthContext/AuthContext";
import { getUnreadCount } from "../utilities/AgentNotificationsApi";

const POLL_INTERVAL_MS = 45000;

type IAgentNotifications = {
  unreadCount: number;
  setUnreadCount: (count: number) => void;
  refreshCount: () => Promise<void>;
  feedOpen: boolean;
  setFeedOpen: (open: boolean) => void;
};

const AgentNotificationsContext = React.createContext<IAgentNotifications>({
  unreadCount: 0,
  setUnreadCount: () => {},
  refreshCount: async () => {},
  feedOpen: false,
  setFeedOpen: () => {},
});

export const AgentNotificationsProvider = ({ children }: { children: React.ReactNode }) => {
  const { auth } = useContext(AuthContext);
  const isAgent = auth?.role === "agent";
  const [unreadCount, setUnreadCount] = useState(0);
  const [feedOpen, setFeedOpen] = useState(false);
  const stopped = useRef(false);

  const refreshCount = useCallback(async () => {
    if (stopped.current) return;
    try {
      setUnreadCount(await getUnreadCount());
    } catch (error: any) {
      // An expired session must not keep polling or spam the user.
      if (error?.response?.status === 401) stopped.current = true;
    }
  }, []);

  useEffect(() => {
    if (!isAgent) {
      setUnreadCount(0);
      setFeedOpen(false);
      return;
    }
    stopped.current = false;
    refreshCount();
    const timer = window.setInterval(() => {
      if (!document.hidden) refreshCount();
    }, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (!document.hidden) refreshCount();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isAgent, refreshCount]);

  return (
    <AgentNotificationsContext.Provider value={{ unreadCount, setUnreadCount, refreshCount, feedOpen, setFeedOpen }}>
      {children}
    </AgentNotificationsContext.Provider>
  );
};

export const useAgentNotifications = () => useContext(AgentNotificationsContext);

export default AgentNotificationsContext;
